import React, { useState, useEffect, useContext, useRef } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
	Select,
	InputLabel,
	MenuItem,
	FormControl,
	TextField,
	InputAdornment,
	IconButton,
  Switch,
  FormControlLabel,
	Typography,
	Grid,
	FormHelperText,
	Box,
	Checkbox,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import ConfirmationModal from '../ConfirmationModal';
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import DoctorSchedulesForm from "../DoctorSchedulesForm";
import ProfessionalTitleSelect from "../ProfessionalTitleSelect";
import WhatsAppSelect from '../WhatsAppSelect';
import SignatureUploader from "../SignatureUploader";

const useStyles = makeStyles(theme => ({
  root: { display: "flex", flexWrap: "wrap" },
  multFieldLine: { display: "flex", "& > *:not(:last-child)": { marginRight: theme.spacing(1) } },
  btnWrapper: { position: "relative" },
  buttonProgress: { color: green[500], position: "absolute", top: "50%", left: "50%", marginTop: -12, marginLeft: -12 },
  formControl: { margin: theme.spacing(1), minWidth: 120 },
  modalLoaderBackdrop: { position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0, 0, 0, 0.3)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1300 },
  dialogActionsSticky: {
    position: "sticky",
    bottom: 0,
    zIndex: 10,
    padding: "8px 16px",
    borderTop: "1px solid rgba(255, 255, 255, 0.1)",
    backgroundColor: "rgba(0, 0, 0, 0.1)",
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  }
}));

const UserModal = ({ open, onClose, userId, lastAdmin }) => {
  const classes = useStyles();
  const { user: loggedInUser } = useContext(AuthContext);
  const [loadingUser, setLoadingUser] = useState(false);
  const [user, setUser] = useState({ 
    name: "", 
    email: "", 
    password: "", 
    profile: "asistente", 
    canViewNumbers: true, 
    canSendAudios: true, 
    professionalTitles: [] 
  });
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [whatsappId, setWhatsappId] = useState('');
	const [professionalTitles, setProfessionalTitles] = useState([]);
  const doctorSchedulesRef = useRef();
	const [signatureFile, setSignatureFile] = useState(null);
	const [confirmOpen, setConfirmOpen] = useState(false);
	
  const UserSchema = Yup.object().shape({
		name: Yup.string()
			.min(2, "¡Demasiado corto!")
			.max(50, "¡Demasiado largo!")
			.required("Este campo es obligatorio"),

		password: Yup.string()
			.transform(v => (v === "" ? undefined : v))
			.min(5, "¡Demasiado corto!")
			.max(50, "¡Demasiado largo!")
			.when("userId", {
				is: v => !v,
				then: schema => schema.required("Este campo es obligatorio"),
				otherwise: schema => schema.notRequired()
			}),

		email: Yup.string()
			.email("Email inválido")
			.required("Este campo es obligatorio"),

		professionalTitles: Yup.array()
			.of(
				Yup.object().shape({
					professionalTitleId: Yup.number().required(),
					titleName: Yup.string(),
					professionalLicense: Yup.string()
						.required("La cédula profesional es obligatoria para cada título."),
					consultationFee: Yup.number()
						.typeError("El costo debe ser un número.")
						.required("El costo de la consulta es obligatorio para cada título.")
						.min(0, "El costo no puede ser negativo."),
				})
			)
			.when("isDoctor", {
				is: true,
				then: schema => schema
					.min(1, "Un doctor debe tener al menos un título profesional seleccionado.")
					.required(),
				otherwise: schema => schema.notRequired(),
			}),

		professionalTitleIds: Yup.array().when("isDoctor", {
			is: true,
			then: schema => schema.min(1, "Se debe seleccionar al menos un título."),
			otherwise: schema => schema.notRequired()
		})
	});

  useEffect(() => {
    const fetchUser = async () => {
      if (!userId) {
        setUser({ name: "", email: "", password: "", profile: "asistente", canViewNumbers: true, canSendAudios: true,  professionalTitles: [] });
        setSelectedQueueIds([]);
        setWhatsappId('');
        return;
      }
      setLoadingUser(true);
      try {
        const { data } = await api.get(`/users/${userId}`);
        setUser(prev => ({
            ...prev,
            ...data,
            professionalTitles: data.professionalTitles || []
        }));
        setSelectedQueueIds(data.queues?.map(q => q.id) || []);
        setWhatsappId(data.whatsappId || '');
				const titlesWithDetails = data.professionalTitles?.map(title => ({
						professionalTitleId: title.id,
						titleName: title.title,
						professionalLicense: title.DoctorProfessionalTitle?.professionalLicense || '',
						consultationFee: title.DoctorProfessionalTitle?.consultationFee || ''
				})) || [];
				setProfessionalTitles(titlesWithDetails);
      } catch (err) {
        toastError(err);
      } finally {
        setLoadingUser(false);
      }
    };
    if (open) {
      setSignatureFile(null);
      fetchUser();
    }
  }, [userId, open]);

  const handleClose = () => {
    onClose();
    setUser({ name: "", email: "", password: "", profile: "asistente", canViewNumbers: true, canSendAudios: true, professionalTitles: [] });
    setSelectedQueueIds([]);
    setWhatsappId('');
		setSignatureFile(null);
  };

  const handleSaveUser = async (values) => {
    const { setSubmitting } = values;

    const userData = {
      ...values,
      whatsappId,
      queueIds: selectedQueueIds,
      professionalTitles: professionalTitles 
    };
		delete userData.professionalTitleIds; 

    try {
      if (values.isDoctor && signatureFile) {
				const formData = new FormData();
				formData.append("signature", signatureFile);
				await api.put(`/users/${userId}/signature`, formData, {
					headers: { "Content-Type": "multipart/form-data" }
				});
			}
			
      const response = userId
        ? await api.put(`/users/${userId}`, userData)
        : await api.post("/users", userData);

      if (
				values.isDoctor &&
				doctorSchedulesRef.current &&
				(loggedInUser.profile === 'admin' || loggedInUser.profile === 'superadmin')
			) {
				await doctorSchedulesRef.current.saveToServer(response.data.id || userId);
			}

      toast.success(i18n.t("userModal.success"));
      handleClose();

    } catch (err) {
      toastError(err);
    }
  };

	const handleDeleteSignature = async () => {
    try {
        const formData = new FormData();
        formData.append('signature', 'null'); 
        
        await api.put(`/users/${userId}/signature`, formData, {
            headers: { "Content-Type": "multipart/form-data" }
        });
        
        toast.success("Firma eliminada correctamente.");
        setUser(prev => ({...prev, signature: null, signatureUrl: null}));
    } catch (err) {
        toastError(err);
    } finally {
        setConfirmOpen(false);
    }
  };
	
  return (
    <div className={classes.root}>
      {loadingUser && (
        <div className={classes.modalLoaderBackdrop}>
          <CircularProgress size={60} />
        </div>
      )}
      <Dialog open={open && !loadingUser} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
        <DialogTitle>{userId ? i18n.t("userModal.title.edit") : i18n.t("userModal.title.add")}</DialogTitle>
        <Formik initialValues={{
            ...user,
            userId: userId || null,
            professionalTitleIds: user.professionalTitles?.map(t => t.id) || [],
						professionalTitles: professionalTitles,
          }} validationSchema={UserSchema} 
        onSubmit={async (values, actions) => {
					await handleSaveUser(values);
					actions.setSubmitting(false);
				}}
				>
          {({ touched, errors, isSubmitting, values, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Field as={TextField} label={i18n.t("userModal.form.name")} autoFocus name="name"
                    error={touched.name && Boolean(errors.name)} helperText={touched.name && errors.name}
                    variant="outlined" margin="dense" fullWidth />
                  <Field as={TextField} name="password" variant="outlined" margin="dense"
                    label={i18n.t("userModal.form.password")} error={touched.password && Boolean(errors.password)}
                    helperText={touched.password && errors.password} type={showPassword ? 'text' : 'password'}
                    InputProps={{ endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(e => !e)}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    )}}
                    fullWidth
                  />
                </div>

                <div className={classes.multFieldLine}>
                  <Field as={TextField} label={i18n.t("userModal.form.email")} name="email"
                    error={touched.email && Boolean(errors.email)} helperText={touched.email && errors.email}
                    variant="outlined" margin="dense" fullWidth />
                  <FormControl variant="outlined" className={classes.formControl} margin="dense">
                    <Can role={loggedInUser.profile} perform="user-modal:editProfile" yes={() => (
                      <>
                        <InputLabel id="profile-selection-input-label">{i18n.t("userModal.form.profile")}</InputLabel>
                        <Field as={Select} labelId="profile-selection-label" id="profile-selection" name="profile"
                          required disabled={lastAdmin && userId} label={i18n.t("userModal.form.profile")}
													onChange={(e) => {
														const newProfile = e.target.value;
														setFieldValue("profile", newProfile);
														if (newProfile === 'doctor') {
															setFieldValue('isDoctor', true);
														}
														if (newProfile === 'asistente') {
															setFieldValue('isDoctor', false);
														}
													}}
												>
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="asistente">Asistente</MenuItem>
                          <MenuItem value="doctor">Doctor</MenuItem>
                          {loggedInUser.profile === "superadmin" && <MenuItem value="superadmin">Superadmin</MenuItem>}
                        </Field>
                      </>
                    )} />
                  </FormControl>
                </div>

								<Can role={loggedInUser.profile} perform="user-modal:editProfile" yes={() => (
									<>
											{values.profile === 'admin' && (
													<FormControlLabel
															control={
																	<Field
																			as={Checkbox}
																			name="isDoctor"
																			color="primary"
																			checked={values.isDoctor || false}
																	/>
															}
															label="Asignar también permisos de Doctor"
													/>
											)}

											{values.profile === 'doctor' && (
												<FormControlLabel
													control={
														<Checkbox
															color="primary"
															checked={values.isDoctor && values.profile === 'admin'}
															onChange={(e) => {
																if (e.target.checked) {
																	setFieldValue("profile", "admin");
																	setFieldValue("isDoctor", true);
																} else {
																	setFieldValue("isDoctor", false);
																	setFieldValue("profile", "doctor");
																}
															}}
														/>
													}
													label="Asignar también permisos de Admin"
												/>
											)}
									</>
								)} />

                <Can role={loggedInUser.profile} perform="user-modal:editQueues" yes={() => (
                  <QueueSelect selectedQueueIds={selectedQueueIds} onChange={setSelectedQueueIds} />
                )} />

                <Can
                  role={loggedInUser.profile}
                  perform="user-modal:editQueues"
                  yes={() => (
                    <WhatsAppSelect
                      value={whatsappId}
                      onChange={setWhatsappId}
                      mode="select"
                    />
                  )}
                />
                
                {(loggedInUser.profile === 'admin' || loggedInUser.profile === 'superadmin') && (
									<>
										<FormControlLabel
											control={
												<Field
													as={Switch}
													checked={values.canViewNumbers}
													onChange={(e) => setFieldValue("canViewNumbers", e.target.checked)}
													name="canViewNumbers"
													color="primary"
												/>
											}
											label="Permitir ver números de contacto"
											style={{ marginTop: 16, marginBottom: 8 }}
										/>
										<FormControlLabel
											control={
												<Field
													as={Switch}
													checked={values.canSendAudios}
													onChange={(e) => setFieldValue("canSendAudios", e.target.checked)}
													name="canSendAudios"
													color="primary"
												/>
											}
											label="Permitir enviar audios"
											style={{ marginTop: 16, marginBottom: 8 }}
										/>
									</>
                )}
                
                {values.isDoctor && (
									<>
										<ProfessionalTitleSelect
											selectedTitlesProp={user?.professionalTitles || []}
											onChange={(newSelectedTitles) => {
												const newTitlesState = newSelectedTitles.map(title => {
														const existingData = professionalTitles.find(t => t.professionalTitleId === title.id);
														return {
																professionalTitleId: title.id,
																titleName: title.title,
																professionalLicense: existingData?.professionalLicense || '',
																consultationFee: existingData?.consultationFee || ''
														};
												});
												setProfessionalTitles(newTitlesState);
												setFieldValue("professionalTitleIds", newSelectedTitles.map(t => t.id));
												setFieldValue("professionalTitles", newTitlesState);
											}}
											error={touched.professionalTitleIds && Boolean(errors.professionalTitleIds)}
											helperText={touched.professionalTitleIds && errors.professionalTitleIds}
										/>
															
										{professionalTitles.map((titleData, index) => (
											<Grid 
												container
												alignItems="center" 
												key={titleData.professionalTitleId} 
												style={{ marginTop: 16 }}
											>
												<Grid item xs={12} sm={4}>
													<Typography variant="subtitle1" align="right" style={{ marginRight: '8px' }}>
														{titleData.titleName}:
													</Typography>
												</Grid>

												<Grid item xs={12} sm={5}>
													<TextField
														label="Cédula Profesional"
														variant="outlined"
														margin="dense"
														value={titleData.professionalLicense}
														onChange={(e) => {
															const newTitles = [...professionalTitles];
															newTitles[index].professionalLicense = e.target.value;
															setProfessionalTitles(newTitles);
															setFieldValue("professionalTitles", newTitles);
														}}
													/>
													{errors.professionalTitles?.[index]?.professionalLicense && touched.professionalTitles?.[index]?.professionalLicense && (
														<FormHelperText error>
															{errors.professionalTitles[index].professionalLicense}
														</FormHelperText>
													)}
												</Grid>

												<Grid item xs={12} sm={3}>
													<TextField
														label="Costo"
														variant="outlined"
														margin="dense"
														type="number"
														value={titleData.consultationFee}
														onChange={(e) => {
															const newTitles = [...professionalTitles];
															newTitles[index].consultationFee = e.target.value;
															setProfessionalTitles(newTitles);
															setFieldValue("professionalTitles", newTitles);
														}}
														InputProps={{
															startAdornment: <InputAdornment position="start">$</InputAdornment>,
														}}
													/>
													{errors.professionalTitles?.[index]?.consultationFee && touched.professionalTitles?.[index]?.consultationFee && (
														<FormHelperText error>
															{errors.professionalTitles[index].consultationFee}
														</FormHelperText>
													)}
												</Grid>
											</Grid>
										))}
									</>
								)}
								{values.isDoctor && (
									<Box mt={2}>
										<Grid container spacing={2}>
											{loggedInUser.id === userId ? (
												<>
													<Grid item xs={12}>
														<Typography variant="h6" gutterBottom>Firma del Doctor</Typography>
														<Typography variant="body2" color="textSecondary" paragraph>
															Sube una imagen de tu firma en formato PNG con fondo transparente. 
															Puedes usar herramientas online como "remove.bg" para prepararla.
														</Typography>
													</Grid>

													<Grid item xs={12}>
														<SignatureUploader
															onChange={(file) => setSignatureFile(file)}
															initialSignatureUrl={user.signatureUrl}
														/>
													</Grid>
												</>
											) : (
												<Grid item xs={12}>
													<Typography variant="h6" gutterBottom>Firma del Doctor</Typography>
													{user.signatureUrl ? (
														<img 
															src={user.signatureUrl} 
															alt="Firma del doctor" 
															style={{ width: 400, height: 150, border: '1px solid grey', objectFit: 'contain', backgroundColor: '#fff' }}
														/>
													) : (
														<Typography color="textSecondary">
															Firma no configurada.
														</Typography>
													)}
												</Grid>
											)}

											{loggedInUser.id === userId && user.signatureUrl && !signatureFile && (
												<Grid item xs={12}>
														<Button 
																variant="outlined"
																color="secondary"
																size="small"
																onClick={() => setConfirmOpen(true)}
														>
																Eliminar Firma Guardada
														</Button>
												</Grid>
											)}
										</Grid>
									</Box>
								)}
                {values.isDoctor && (loggedInUser.profile === 'admin' || loggedInUser.profile === 'superadmin') && (
									<>
										<DoctorSchedulesForm ref={doctorSchedulesRef} doctorId={user?.id || null} />
									</>
								)}
              </DialogContent>

              <DialogActions className={classes.dialogActionsSticky}>
                <Button onClick={handleClose} color="secondary" disabled={isSubmitting} variant="outlined">
                  {i18n.t("userModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {userId ? i18n.t("userModal.buttons.okEdit") : i18n.t("userModal.buttons.okAdd")}
                  {isSubmitting && <CircularProgress size={24} className={classes.buttonProgress} />}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
				<ConfirmationModal
					title="Eliminar Firma"
					open={confirmOpen}
					onClose={() => setConfirmOpen(false)}
					onConfirm={handleDeleteSignature}
				>
					¿Estás seguro de que deseas eliminar tu firma guardada? Esta acción no se puede deshacer.
				</ConfirmationModal>
      </Dialog>
    </div>
  );
};

export default UserModal;